import { useImportDuoInvoiceResponseMutation } from "@/api/mutations/duo/useImportDuoInvoiceResponseMutation"
import { useDuoInvoiceResponsesInfiniteQuery } from "@/api/queries/duo/useDuoInvoiceResponsesInfiniteQuery"
import { DuoSidebarActionButton } from "@/components/duo/DuoSidebarActionButton"
import { DuoWorkspaceLayout } from "@/components/duo/DuoWorkspaceLayout"
import { InvoiceResponseDetail } from "@/components/duo/InvoiceResponseDetail"
import {
  DUO_IMPORT_ACCEPT,
  DUO_IMPORT_MAX_FILE_SIZE,
} from "@/components/duo/duoImportFileConfig"
import { getImportErrorMessage } from "@/components/duo/invoiceResponseUtils"
import { checkPermissions } from "@/utils/checkPermissions"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useState } from "react"
import {
  Button,
  DropZone,
  PageHeader,
  Skeleton,
  cn,
  formatDateToDutch,
} from "toptaal-shared"
import { z } from "zod"

const searchSchema = z.object({
  responseId: z.number().optional(),
})

export const Route = createFileRoute("/duo/terugmeldingen/")({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: () => checkPermissions("read", "duo"),
})

function RouteComponent() {
  const { responseId } = useSearch({ from: "/duo/terugmeldingen/" })
  const navigate = useNavigate({ from: "/duo/terugmeldingen/" })
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDuoInvoiceResponsesInfiniteQuery()
  const responses = historyData?.responses ?? []

  const selectResponse = (selectedResponseId: number) => {
    navigate({ search: { responseId: selectedResponseId }, replace: true })
  }

  const openImportForm = () => {
    navigate({ search: {}, replace: true })
  }

  return (
    <DuoWorkspaceLayout
      sidebar={
        <>
          <PageHeader>DUO terugmeldingen</PageHeader>

          <DuoSidebarActionButton
            aria-current={responseId === undefined ? "page" : undefined}
            onClick={openImportForm}
          >
            Nieuwe terugmeldingen
          </DuoSidebarActionButton>

          <div className="mt-3 border-t border-primary-accent-blue pt-3">
            {isHistoryLoading && <ResponseHistorySkeleton />}

            {isHistoryError && (
              <p className="text-sm text-red" role="alert">
                De importgeschiedenis kon niet worden geladen.
              </p>
            )}

            {!isHistoryLoading && !isHistoryError && responses.length === 0 && (
              <p className="py-3 text-sm text-primary-dark-blue/70">
                Er zijn nog geen terugmeldingen geïmporteerd.
              </p>
            )}

            <div className="flex flex-col gap-2">
              {responses.map((response) => {
                const isSelected = responseId === response.id

                return (
                  <button
                    key={response.id}
                    type="button"
                    aria-current={isSelected ? "page" : undefined}
                    className={cn(
                      "flex h-11 w-full items-center justify-between rounded px-4 text-left transition-colors",
                      isSelected
                        ? "bg-primary-green text-white"
                        : "bg-light-grey hover:bg-primary-light-blue",
                    )}
                    onClick={() => selectResponse(response.id)}
                  >
                    <span>{formatDateToDutch(response.insertedAt)}</span>
                    <ChevronRightIcon className="size-5" />
                  </button>
                )
              })}

              {hasNextPage && (
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-1 w-full"
                  isLoading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  Meer laden
                </Button>
              )}
            </div>
          </div>
        </>
      }
    >
      {responseId === undefined ? (
        <InvoiceResponseImportForm
          onImported={(importedResponseId) =>
            navigate({
              search: { responseId: importedResponseId },
              replace: true,
            })
          }
        />
      ) : (
        <InvoiceResponseDetail responseId={responseId} />
      )}
    </DuoWorkspaceLayout>
  )
}

function InvoiceResponseImportForm({
  onImported,
}: {
  onImported: (responseId: number) => void
}) {
  const [files, setFiles] = useState<File[]>([])
  const mutation = useImportDuoInvoiceResponseMutation()
  const errorMessage = getImportErrorMessage(mutation.error)

  const importFile = () => {
    const [file] = files
    if (!file) return

    mutation.mutate(file, {
      onSuccess: (response) => onImported(response.data.id),
    })
  }

  return (
    <div className="mx-auto max-w-2xl pt-36">
      <p className="mb-5 text-center font-bold text-primary-dark-blue">
        Importeer terugmeldingen van DUO in MTT
      </p>

      <DropZone
        variant="white"
        accept={DUO_IMPORT_ACCEPT}
        maxFileSize={DUO_IMPORT_MAX_FILE_SIZE}
        selectedDocumentType="other"
        handleAddFiles={(newFiles) => setFiles(newFiles.slice(-1))}
        handleRemoveFile={() => setFiles([])}
      />

      {errorMessage && (
        <p
          className="mt-4 rounded border border-red/30 bg-red/5 p-3 text-sm text-red"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      <div className="mt-6 flex justify-center">
        <Button
          type="button"
          isLoading={mutation.isPending}
          disabled={files.length === 0}
          onClick={importFile}
        >
          Importeren
        </Button>
      </div>
    </div>
  )
}

function ResponseHistorySkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Importgeschiedenis laden">
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-11 w-full" />
      ))}
    </div>
  )
}
